import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import {
  initSentryClient,
  captureClientError,
  setSentryUser,
  setSentryRole,
  setSentryTags,
  addClientBreadcrumb,
} from "../lib/sentry-client";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { captureAthleteRef } from "@/lib/athlete-ref";
import { useSmoothHashScroll } from "@/hooks/use-smooth-hash-scroll";


/** Tags Sentry events with the signed-in user's app role (admin vs volunteer). */
async function tagRole(userId: string) {
  try {
    const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    await setSentryRole(data === true ? "admin" : "volunteer");
  } catch {
    await setSentryRole("volunteer");
  }
}

function NotFoundComponent() {
  return (
    <div className="app-light flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    const route = typeof window !== "undefined" ? window.location.pathname : "unknown";
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    void setSentryTags({ boundary: "tanstack_root_error_component", route });
    void captureClientError(error, {
      boundary: "tanstack_root_error_component",
      route,
      critical: true,
    });
  }, [error]);

  return (
    <div className="app-light flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Warrior Volunteer" },
      {
        name: "description",
        content: "The volunteer operations workspace for T1D Warriors.",
      },
      { name: "author", content: "T1D Warriors" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Warrior Volunteer" },
      { name: "twitter:card", content: "summary_large_image" },
    ],

    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap",
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useSmoothHashScroll();
  useEffect(() => {
    captureAthleteRef(window.location.search, window.localStorage);
  }, []);

  useEffect(() => {
    void initSentryClient();
    void supabase.auth.getUser().then(({ data }) => {
      if (!data.user) {
        void setSentryRole(null);
        return;
      }
      void setSentryUser({ id: data.user.id, email: data.user.email });
      void tagRole(data.user.id);
    });
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      void setSentryUser(
        session?.user ? { id: session.user.id, email: session.user.email } : null,
      );
      if (session?.user) void tagRole(session.user.id);
      else void setSentryRole(null);
      void addClientBreadcrumb(`auth:${event.toLowerCase()}`, undefined, "auth");
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  );
}

